import { createContext, useState, useContext, useEffect } from 'react';
import { supabase } from '../../supabase/supabaseClient';
import { useAuth } from '../../contexts/AuthContext';


const ColorContext = createContext();

export const useColorContext = () => useContext(ColorContext);

export const ColorProvider = ({ children }) => {
  const [savedPalettes, setSavedPalettes] = useState([]);
  const [currentPaletteToView, setCurrentPaletteToView] = useState(null);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();


  useEffect(() => {
    if (user) {
      loadPalettesFromSupabase();
    } else {
      // Clear palettes when user logs out
      setSavedPalettes([]);
      setCurrentPaletteToView(null);
    }
  }, [user]);

  /////////////////

  // NEW SUPABASE
  const loadPalettesFromSupabase = async () => {
    if (!user) return;

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('color_palettes')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error loading palettes:', error);
        return;
      }

      setSavedPalettes(data || []);
    } catch (error) {
      console.error('Error loading palettes:', error);
    } finally {
      setLoading(false);
    }
  };

  const savePalette = async (newPalette) => {
    if (!user) {
      // console.error('User must be logged in to save palettes');
      return false; // Return false to indicate failure
    }

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('color_palettes')
        .insert([
          {
            user_id: user.id,
            name: newPalette.name,
            palette_data: newPalette, // Store the entire palette object as JSON
            created_at: new Date().toISOString()
          }
        ])
        .select();

      if (error) {
        console.error('Error saving palette:', error);
        return false;
      }

      // Add the new palette to the local state
      setSavedPalettes(prev => [data[0], ...prev]);

      return true; // Return true to indicate success
    } catch (error) {
      console.error('Error saving palette:', error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const removePalette = async (paletteId) => {
    if (!user) {
      console.error('User must be logged in to remove palettes');
      return false;
    }

    setLoading(true);
    try {
      const { error } = await supabase
        .from('color_palettes')
        .delete()
        .eq('id', paletteId)
        .eq('user_id', user.id); // Ensure user can only delete their own palettes

      if (error) {
        console.error('Error removing palette:', error);
        return false;
      }

      // Remove from local state
      setSavedPalettes(prev => prev.filter(palette => palette.id !== paletteId));
      return true;
    } catch (error) {
      console.error('Error removing palette:', error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const setPaletteToView = (palette) => {
    // If palette comes from Supabase, we need to extract the palette_data
    const paletteData = palette.palette_data || palette;
    setCurrentPaletteToView(paletteData);
    localStorage.setItem('currentPaletteToView', JSON.stringify(paletteData));
  };

  // Clear palette to view
  const clearPaletteToView = () => {
    setCurrentPaletteToView(null);
    localStorage.removeItem('currentPaletteToView');
  };

  // Load palette to view from localStorage on initial load (this can stay for UX)
  useEffect(() => {
    const storedPaletteToView = localStorage.getItem('currentPaletteToView');
    if (storedPaletteToView) {
      try {
        setCurrentPaletteToView(JSON.parse(storedPaletteToView));
      } catch (error) {
        console.error("Error parsing palette to view:", error);
      }
    }
  }, []);







  return (

    <ColorContext.Provider value={{
      savedPalettes,
      savePalette,
      removePalette,
      currentPaletteToView,
      setPaletteToView,
      clearPaletteToView,
      loading,
      isLoggedIn: !!user
    }}>
      {children}
    </ColorContext.Provider>
  );
};